import {
  PosterStudioCloseRefused,
  PosterStudioClosed,
  PosterStudioError,
  PosterStudioEvent,
  PosterStudioExit,
  PosterStudioHostEnvelope,
  PosterStudioReady,
  PosterStudioSave,
  PosterStudioShare,
  kPosterStudioProtocolVersion,
  kPosterStudioSignInErrors,
} from '@euler/model/poster-studio';
import { useServiceFactory } from '@euler/services/factory';
import { useBehaviorSubject, useCreation } from '@euler/utils/hooks';
import { useCallback, useEffect } from 'react';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  defer,
  filter,
  firstValueFrom,
  map,
  merge,
  race,
  switchMap,
  take,
  takeUntil,
  timer,
} from 'rxjs';
import { deliverPosters, kPosterStudioCapabilities } from './deliverPosters';

export interface PosterStudioPhaseConnecting {
  kind: 'connecting';
}
export interface PosterStudioPhaseReady {
  kind: 'ready';
}
export interface PosterStudioPhaseFailed {
  kind: 'failed';
  message: string;
}
export type PosterStudioPhase =
  | PosterStudioPhaseConnecting
  | PosterStudioPhaseReady
  | PosterStudioPhaseFailed;

export interface PosterStudioMayClose {
  kind: 'close';
}
/** The studio's reason to stay, in the shop's words. */
export interface PosterStudioWantsToStay {
  kind: 'refused';
  message: string;
}
export type PosterStudioCloseAnswer =
  | PosterStudioMayClose
  | PosterStudioWantsToStay;

/** The protocol asks a host to repeat `init` until `ready`. */
const kInitRetryMs = 300;
/** Past this without `ready`, the studio is not coming. */
const kReadyTimeoutMs = 30_000;
/**
 * A close request the studio does not answer - mid-navigation - closes. Long
 * enough for a phone busy encoding a poster to answer.
 */
const kCloseAnswerTimeoutMs = 5000;

const kUnreachable = '无法打开朋友圈海报，请重试';
const kMayClose: PosterStudioMayClose = { kind: 'close' };

function isReady(event: PosterStudioEvent): event is PosterStudioReady {
  return event.kind === 'ready';
}

function isExit(event: PosterStudioEvent): event is PosterStudioExit {
  return event.kind === 'exit';
}

function isSignInFailure(event: PosterStudioEvent): event is PosterStudioError {
  return (
    event.kind === 'error' &&
    kPosterStudioSignInErrors.some(code => code === event.code)
  );
}

function isDelivery(
  event: PosterStudioEvent,
): event is PosterStudioShare | PosterStudioSave {
  return event.kind === 'share' || event.kind === 'save';
}

function isCloseAnswer(
  requestId: string,
): (
  event: PosterStudioEvent,
) => event is PosterStudioClosed | PosterStudioCloseRefused {
  return (event): event is PosterStudioClosed | PosterStudioCloseRefused =>
    (event.kind === 'closed' || event.kind === 'close-refused') &&
    event.requestId === requestId;
}

/**
 * The studio's session from euler's side: a fresh one-time token, `init`
 * repeated until `ready`, the pictures it hands over, and close requests.
 * Mounted once per attempt - a retry remounts it with a new token.
 */
export function usePosterStudioSession({
  orgId,
  storeId,
}: {
  orgId: number;
  storeId: number;
}) {
  const { posterStudioService } = useServiceFactory();
  const events$ = useCreation(() => new Subject<PosterStudioEvent>(), []);
  const toStudio$ = useCreation(
    () => new Subject<PosterStudioHostEnvelope>(),
    [],
  );
  const phase$ = useCreation(
    () => new BehaviorSubject<PosterStudioPhase>({ kind: 'connecting' }),
    [],
  );
  const [phase] = useBehaviorSubject(phase$);
  /** The studio's ‹: the shop is done with it. */
  const exits$ = useCreation(() => events$.pipe(filter(isExit)), [events$]);

  useEffect(() => {
    const ready$ = events$.pipe(filter(isReady), take(1));
    const signInFailed$ = events$.pipe(filter(isSignInFailure), take(1));
    const fail = (message: string) => {
      if (phase$.getValue().kind !== 'failed')
        phase$.next({ kind: 'failed', message });
    };
    const inits$: Observable<PosterStudioHostEnvelope> = defer(() =>
      posterStudioService.issueToken(),
    ).pipe(
      switchMap(grant =>
        timer(0, kInitRetryMs).pipe(
          map(
            (): PosterStudioHostEnvelope => ({
              v: kPosterStudioProtocolVersion,
              type: 'init',
              payload: {
                token: grant.token,
                orgId,
                storeId,
                locale: 'zh-CN',
                theme: 'light',
                capabilities: kPosterStudioCapabilities,
              },
            }),
          ),
          takeUntil(merge(ready$, signInFailed$)),
        ),
      ),
    );
    const subscription = new Subscription();
    subscription.add(
      inits$.subscribe({
        next: envelope => toStudio$.next(envelope),
        error: (error: unknown) => {
          console.error('poster studio: issuing a token failed:', error);
          fail(kUnreachable);
        },
      }),
    );
    subscription.add(
      ready$.subscribe(() => {
        if (phase$.getValue().kind === 'connecting')
          phase$.next({ kind: 'ready' });
      }),
    );
    // The studio answers `init` at once and signs in after, so a refused
    // sign-in can arrive after `ready`.
    subscription.add(signInFailed$.subscribe(event => fail(event.message)));
    subscription.add(
      timer(kReadyTimeoutMs)
        .pipe(takeUntil(merge(ready$, signInFailed$)))
        .subscribe(() => fail(kUnreachable)),
    );
    subscription.add(
      events$
        .pipe(filter(isDelivery))
        .subscribe(event => void deliverPosters(event)),
    );
    subscription.add(
      events$.subscribe(event => {
        if (event.kind === 'error')
          console.error(`poster studio: ${event.code}: ${event.message}`);
      }),
    );
    return () => subscription.unsubscribe();
  }, [posterStudioService, orgId, storeId, events$, toStudio$, phase$]);

  const onEvent = useCallback(
    (event: PosterStudioEvent) => events$.next(event),
    [events$],
  );

  /** Asks the studio whether it may close; it may at once if it never opened. */
  const requestClose = useCallback(async () => {
    if (phase$.getValue().kind !== 'ready') return kMayClose;
    const requestId = `close-${Date.now()}`;
    const answer = firstValueFrom(
      race(
        events$.pipe(
          filter(isCloseAnswer(requestId)),
          map(
            (event): PosterStudioCloseAnswer =>
              event.kind === 'closed'
                ? kMayClose
                : { kind: 'refused', message: event.message },
          ),
        ),
        timer(kCloseAnswerTimeoutMs).pipe(map(() => kMayClose)),
      ),
    );
    toStudio$.next({
      v: kPosterStudioProtocolVersion,
      type: 'close-request',
      payload: {},
      requestId,
    });
    return await answer;
  }, [phase$, events$, toStudio$]);

  return { phase, toStudio$, exits$, onEvent, requestClose };
}
